import { useState, useEffect } from 'react';
import { getOpenAiApiKey } from '@/lib/api-keys';
import { promptsService } from '@/lib/supabase/prompts';
import { usePrompt } from '@/contexts/PromptContext';

// Interfaces para a resposta da API
interface OpenAIError {
  message: string;
  type: string;
  code: string;
}

// Interface para a resposta do ChatGPT
export interface ChatCompletionResponse {
  id: string;
  object: string;
  created: number;
  model: string;
  choices: Array<{
    index: number;
    message: {
      role: string;
      content: string;
    };
    finish_reason: string;
  }>;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

// Interface para a resposta estruturada de um passeio
export interface ExcursionInfo {
  description: string;
  location: {
    name: string;
    address: string;
    googleMapsUrl: string;
  };
  additionalInfo?: {
    bestTimeToVisit?: string;
    estimatedDuration?: string;
    tips?: string;
  };
}

// Função para log detalhado de erros
const logApiError = (error: any, context: { prompt?: string; model?: string; }) => {
  console.error('Erro na API da OpenAI:', {
    error,
    context
  });
};

// Função para obter mensagem de erro formatada
const getErrorMessage = (error: any): string => {
  if (typeof error === 'string') {
    return error;
  }
  
  if (error?.error?.message) {
    return `Erro: ${error.error.message}`;
  }
  
  if (error?.message) {
    return `Erro: ${error.message}`;
  }
  
  return 'Ocorreu um erro desconhecido ao acessar a API da OpenAI';
};

export function useOpenAiApi() {
  const [isConfigured, setIsConfigured] = useState<boolean>(false);
  const [systemRole, setSystemRole] = useState<string>('Você é um assistente de viagem especializado em fornecer informações precisas sobre destinos turísticos.');
  
  // Use prompts from context instead of local state
  const toneOfVoice = usePrompt('tone_of_voice');
  
  useEffect(() => {
    const apiKey = getOpenAiApiKey();
    setIsConfigured(!!apiKey && apiKey.trim() !== '');
  }, []);

  // Função para fazer uma chamada para o ChatGPT
  const chatCompletion = async (
    prompt: string,
    model: string = 'gpt-4-turbo',
    temperature: number = 0.7,
    maxTokens: number = 1500
  ): Promise<ChatCompletionResponse> => {
    const apiKey = getOpenAiApiKey();
    
    if (!apiKey) {
      throw new Error('API Key da OpenAI não configurada');
    }
    
    try {
      // Use the tone directly from the context
      let systemMessage = systemRole;
      
      if (toneOfVoice && toneOfVoice.trim() !== '') {
        systemMessage += ` ${toneOfVoice}`;
      }
      
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model,
          messages: [
            {
              role: 'system',
              content: systemMessage
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature,
          max_tokens: maxTokens
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw data;
      }
      
      return data;
    } catch (error) {
      logApiError(error, { prompt, model });
      throw new Error(getErrorMessage(error));
    }
  };
  
  // Função específica para obter informações sobre um passeio
  const getExcursionInfo = async (excursionName: string): Promise<ExcursionInfo> => {
    try {
      // Get the prompt from context instead of service
      const tourDescriptionPrompt = usePrompt('tour_description');
      
      // Determine the prompt to use (from context or default)
      let promptTemplate = tourDescriptionPrompt || `
        Escreva uma descrição detalhada de um passeio turístico chamado "{tourName}". 
        A descrição deve incluir informações relevantes sobre as atrações, história, experiências e o que os turistas podem esperar durante a visita. 
        Seja informativo, envolvente e persuasivo. Mantenha um tom profissional e adequado para um site de turismo. 
        A descrição deve ter entre 150 e 300 palavras. Não inclua horários específicos ou preços.
      `;
      
      // Substituir a variável no template
      const finalPrompt = promptTemplate.replace(/{tourName}/g, excursionName);
      
      // Adicionar o formato de resposta
      const prompt = `
        ${finalPrompt}
        
        Responda no formato JSON com a seguinte estrutura:
        {
          "description": "Uma descrição detalhada do local, incluindo sua importância histórica ou cultural",
          "location": {
            "name": "Nome oficial do local",
            "address": "Endereço completo",
            "googleMapsUrl": "URL do Google Maps para este local"
          },
          "additionalInfo": {
            "bestTimeToVisit": "Melhor período para visitar",
            "estimatedDuration": "Tempo estimado para a visita",
            "tips": "Dicas úteis para os visitantes"
          }
        }
      `;
      
      const response = await chatCompletion(prompt, 'gpt-4-turbo', 0.7, 1000);
      
      try {
        if (response.choices && response.choices.length > 0) {
          const content = response.choices[0].message.content;
          const data = JSON.parse(content);
          return data;
        } else {
          throw new Error('Resposta inválida da API');
        }
      } catch (parseError) {
        console.error('Erro ao processar resposta JSON:', parseError);
        throw new Error('Não foi possível processar a resposta da OpenAI no formato esperado. Por favor, tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao obter informações do passeio:', error);
      throw error;
    }
  };

  // Function to test the API connection
  const testConnection = async (): Promise<boolean> => {
    try {
      const apiKey = getOpenAiApiKey();
      
      if (!apiKey) {
        throw new Error('API Key da OpenAI não configurada');
      }
      
      // Simply check if we have a valid API key
      return true;
    } catch (error) {
      logApiError(error, { prompt: 'Teste de conexão' });
      throw new Error(getErrorMessage(error));
    }
  };

  return {
    isConfigured,
    chatCompletion,
    getExcursionInfo,
    testConnection
  };
} 